import { LuMapPin, LuPhone } from "react-icons/lu";

import { business, serviceAreas } from "../../content/site";

/**
 * Service area section.
 *
 * The footprint is a corridor, not a radius: Fort Worth on the west end,
 * east through Dallas, out to Greenville. Work is concentrated in three
 * hot spots (Greenville, Rockwall, Dallas), so this section leads with
 * those and presents the rest as coverage along the corridor.
 *
 * All of it is driven by `serviceAreas` in src/content/site.js. Do not
 * hardcode a city name into this component — add or remove it there.
 *
 * `serviceAreas.verified` still gates the detailed city list. It is `true`
 * now that the owner has confirmed the corridor. If the list ever needs to
 * come down pending a re-check, flip that flag back to `false` in site.js
 * and this falls back to the corridor statement plus a call-to-confirm.
 *
 * MAP EMBED SLOT
 * A real map with pins along the corridor would beat both reference
 * competitors (one uses a static graphic, the other has no map at all).
 * It is deliberately not here yet because it needs: an API key in an env
 * var, a decision about the third-party request and consent, and a lazy
 * load so it does not cost mobile users on first paint. When it lands it
 * needs an accessible name, a fixed aspect ratio, and the text city list
 * kept underneath it as the accessible equivalent.
 */

const showCityList =
  serviceAreas.verified === true &&
  Array.isArray(serviceAreas.corridorCities) &&
  serviceAreas.corridorCities.length > 0;

const ServiceAreas = () => (
  <section
    id="service-areas"
    aria-labelledby="service-areas-heading"
    className="section bg-surface-sunken"
  >
    <div className="container-page">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-14">
        <header className="min-w-0 max-w-prose">
          <p className="kicker">Where we work</p>
          <h2 id="service-areas-heading" className="mt-3 text-display text-ink">
            From Fort Worth to Greenville
          </h2>
          <p className="mt-4 text-body-lg text-ink-muted">
            We run the {serviceAreas.corridor} corridor. Most of our work sits
            in {serviceAreas.focus.slice(0, -1).join(", ")} and{" "}
            {serviceAreas.focus[serviceAreas.focus.length - 1]} — if you are in
            one of those, we are probably already working nearby today.
          </p>

          {/* Focus areas get visual weight. Everything else is coverage. */}
          <ul className="mt-6 flex flex-wrap gap-2" aria-label="Primary service areas">
            {serviceAreas.focus.map((city) => (
              <li
                key={city}
                className="inline-flex min-w-0 items-center gap-2 rounded-pill border border-line-brand bg-surface-brand-soft px-4 py-2 text-body-sm font-semibold text-ink-brand"
              >
                <LuMapPin aria-hidden="true" className="h-4 w-4 shrink-0" />
                <span className="[overflow-wrap:anywhere]">{city}</span>
              </li>
            ))}
          </ul>

          <a
            href={business.phone.href}
            className="btn-urgent mt-8 w-full sm:w-auto"
          >
            <LuPhone aria-hidden="true" className="h-5 w-5 shrink-0" />
            <span className="tabular">Call {business.phone.display}</span>
          </a>
          <p className="mt-3 text-body-sm text-ink-subtle">
            Not sure you are in range? Call with your address and we will tell
            you straight away.
          </p>
        </header>

        <div className="min-w-0">
          {showCityList ? (
            <div className="card p-6 sm:p-8">
              <h3 className="text-heading text-ink">Along the corridor</h3>
              <p className="mt-2 text-body-sm text-ink-muted">
                A sample of where we get called, west to east. This is not an
                exhaustive list.
              </p>

              <div className="mt-6 space-y-6">
                {serviceAreas.corridorCities.map((band) => (
                  <div key={band.group} className="min-w-0">
                    <h4 className="text-label uppercase text-ink-brand">
                      {band.group}
                    </h4>
                    <ul className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                      {band.cities.map((city) => (
                        <li
                          key={city}
                          className="min-w-0 text-body text-ink-muted [overflow-wrap:anywhere]"
                        >
                          {city}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Fallback when the corridor has not been re-verified. */
            <div className="card border-line-brand bg-surface-brand-soft p-6 sm:p-8">
              <h3 className="text-heading text-ink">
                Check coverage for your address
              </h3>
              <p className="mt-3 text-body text-ink-muted">
                Rather than publish a city list we have not double-checked, we
                would rather confirm it on the phone. Give us the address and we
                will tell you whether we cover it before anything gets booked.
              </p>
              <a
                href={business.phone.href}
                className="btn-outline mt-5 w-full sm:w-auto"
              >
                <LuPhone aria-hidden="true" className="h-5 w-5 shrink-0" />
                <span>Confirm coverage</span>
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  </section>
);

export default ServiceAreas;
